import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Link as MuiLink,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { EMAIL_TEXT } from '../../constants';

const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;

  const iosStandalone = (window.navigator as any).standalone === true;

  const displayModeStandalone =
    window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;

  return iosStandalone || displayModeStandalone;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function Footer() {
  const [installOpen, setInstallOpen] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);

  const ios = React.useMemo(() => isIOS(), []);
  const [installed, setInstalled] = React.useState(false);

  React.useEffect(() => {
    setInstalled(isStandalone());

    const onAppInstalled = () => setInstalled(true);
    window.addEventListener('appinstalled', onAppInstalled);

    const onBeforeInstall = (e: Event) => {
      e.preventDefault?.();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall as any);

    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall as any);
    };
  }, []);

  const canPrompt = !!deferredPrompt;

  const openInstall = () => setInstallOpen(true);
  const closeInstall = () => setInstallOpen(false);

  const handleInstallClick = async () => {
    if (installed) return;

    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } finally {
        setDeferredPrompt(null);
        closeInstall();
      }
      return;
    }

    openInstall();
  };

  return (
    <>
      <Box
        component="footer"
        sx={{
          backgroundColor: '#f5f5f5',
          py: 3,
          mt: '50px',
          borderTop: '1px solid #ddd',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © 2025 Savetorich.
          </Typography>

          <Stack spacing={1} alignItems="center" color="text.secondary">
            <Stack
              direction="row"
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <MuiLink
                component={RouterLink}
                to="https://t.me/savetorich"
                underline="hover"
                color="inherit"
              >
                Новостной телеграм-канал
              </MuiLink>

              <MuiLink
                component="button"
                type="button"
                onClick={handleInstallClick}
                underline="hover"
                color="inherit"
                sx={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  font: 'inherit',
                  opacity: installed ? 0.6 : 1,
                  cursor: installed ? 'default' : 'pointer',
                }}
                aria-disabled={installed}
                title={installed ? 'Приложение уже установлено' : 'Установить приложение'}
              >
                {installed ? 'Приложение установлено ✅' : 'Установить приложение'}
              </MuiLink>
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <MuiLink
                component={RouterLink}
                to="/privacy-policy"
                underline="hover"
                color="inherit"
              >
                Политика персональных данных
              </MuiLink>

              <MuiLink component={RouterLink} to="/cookie-policy" underline="hover" color="inherit">
                Политика cookie
              </MuiLink>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            <a href={`mailto:${EMAIL_TEXT}`}>{EMAIL_TEXT}</a>
          </Typography>
        </Container>
      </Box>

      <Dialog open={installOpen} onClose={closeInstall} fullWidth maxWidth="sm">
        <DialogTitle>Как установить Savetorich</DialogTitle>

        <DialogContent dividers>
          {installed ? (
            <Typography variant="body2" color="text.secondary">
              Приложение уже установлено ✅
            </Typography>
          ) : canPrompt ? (
            <Typography variant="body2" color="text.secondary">
              Нажмите “Установить” — браузер покажет системное окно установки.
            </Typography>
          ) : ios ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                iPhone / iPad (Safari):
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1) Нажмите “Поделиться” (иконка ⤴)
                <br />
                2) Выберите “На экран «Домой»”
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Если кнопки “Установить” нет:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Android (Chrome): меню ⋮ → “Установить приложение” / “Add to Home screen”
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeInstall}>Закрыть</Button>

          {canPrompt && !installed && (
            <Button
              variant="contained"
              onClick={async () => {
                if (!deferredPrompt) return;
                await deferredPrompt.prompt();
                await deferredPrompt.userChoice;
                setDeferredPrompt(null);
                closeInstall();
              }}
            >
              Установить
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Footer;
